import { Button, Modal, Text } from '$components'
import type { SmsCreditBundle } from '$api-client/types.gen'
import { cn } from '$utils'
import { getCurrencySymbol } from '$utils/currency'
import { useEffect, useState } from 'react'

type SmsCreditsTopUpModalProps = {
  bundles: SmsCreditBundle[]
  isOpen: boolean
  isLoading: boolean
  onClose: () => void
  onConfirm: (credits: number) => void
}

function formatBundlePrice(bundle: SmsCreditBundle): string {
  const symbol = getCurrencySymbol(bundle.currency)
  const amount = (bundle.price_in_cents / 100).toFixed(2)

  return `${symbol}${amount}`
}

export function SmsCreditsTopUpModal({
  bundles,
  isOpen,
  isLoading,
  onClose,
  onConfirm,
}: SmsCreditsTopUpModalProps) {
  const [selectedCredits, setSelectedCredits] = useState<number | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setSelectedCredits(null)
      return
    }

    setSelectedCredits(bundles[0]?.credits)
  }, [isOpen, bundles])

  if (!isOpen) {
    return null
  }

  const handleConfirm = () => {
    if (selectedCredits === null) {
      return
    }

    onConfirm(selectedCredits)
  }

  return (
    <Modal onClose={onClose}>
      <Modal.Header>
        <Modal.Heading>
          Top up SMS credits
        </Modal.Heading>

        <Text
          type="s"
          className="text-gray-500"
        >
          Choose a credit bundle to purchase. You&apos;ll be redirected to Stripe to complete payment.
        </Text>
      </Modal.Header>

      <Modal.Content>
        <div className="flex flex-col gap-3">
          {bundles.map((bundle) => {
            const isSelected = selectedCredits === bundle.credits

            return (
              <button
                key={bundle.credits}
                type="button"
                onClick={() => setSelectedCredits(bundle.credits)}
                className={cn(
                  'flex w-full items-center justify-between gap-4 rounded-xl border p-4 text-left transition-colors',
                  isSelected
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-100 hover:bg-gray-50',
                )}
              >
                <Text
                  type="s"
                  weight="medium"
                >
                  {bundle.credits} credits
                </Text>

                <Text
                  type="s"
                  weight="medium"
                >
                  {formatBundlePrice(bundle)}
                </Text>
              </button>
            )
          })}
        </div>
      </Modal.Content>

      <Modal.Footer className="gap-3">
        <Button
          variant="ghost"
          size="lg"
          onClick={onClose}
          disabled={isLoading}
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          size="lg"
          onClick={handleConfirm}
          disabled={selectedCredits === null || isLoading}
          isLoading={isLoading}
        >
          Continue to checkout
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
